import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useContext,
} from "react";
import Valuecards from "../../components/Cards/Valuecards";
import Contentcards from "../../components/Cards/Contentcards";
import Confcard from "../../components/Cards/Confcard";
import Delayreasoncard from "../../components/Cards/Delayreasoncard";
import TanStackTable from "../../components/Table/ServiceTabe";
import { createColumnHelper } from "@tanstack/react-table";
import {
  fetchMonths,
  fetchTotalChassis,
  fetchTotalJC,
  fetchTatPromiseAchievd,
  fetchTatPromiseNOTAchievd,
  fetchJobCardDelayResonData,
} from "../servicePerformance/kpiService";
import { debounce } from "lodash";
import { FilterContext } from "../../context/Filter";
import Contentcards2 from "../../components/Cards/Contentcards2";

const ServicePage = () => {
  const [months, setMonthsData] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [chassisCount, setChassisCount] = useState(null);
  const [jcCount, setJcCount] = useState("");
  const [tatPromiseAchievd, setTatPromiseAchievd] = useState("");
  const [tatPromisenOTAchievd, setTatPromisenOTAchievd] = useState("");
  const [JobCardDelayResonData, setJobCardDelayReson] = useState("");
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { open, setOpen } = useContext(FilterContext)

  const [currentMonth, setCurrentMonth] = useState("");

  const dropdownRef = useRef(null);

  const { superArn: parArn, childArn, isFilterReady } = useContext(FilterContext);

  // Get current month helper - memoized
  const getCurrentMonth = useCallback(() => {
    const date = new Date();
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return monthNames[date.getMonth()];
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch months and set them
  const fetchMonth = useCallback(async () => {
    try {
      setLoading(true);
      const apiData = await fetchMonths();
      if (!apiData) {
        console.error("API response is empty or invalid:", apiData);
        return;
      }
      setMonthsData(apiData);
    } catch (error) {
      console.error("Error fetching month data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Chassis count fetcher - now handles multiple months
  const fetchChassis = useCallback(async (parArn, childArn, monthsToFetch) => {
    const apiData = await fetchTotalChassis(parArn, childArn, monthsToFetch);

    let count = 0;
    if (apiData?.length === 1) {
      count = apiData[0]?.unique_vehicle_count?.value || 0;
    } else if (apiData?.length > 1) {
      count = apiData.reduce((sum, month) => {
        return sum + (month?.unique_vehicle_count?.value || 0);
      }, 0);
    }
    setChassisCount(count);
    setHasData(count > 0);
  }, []);

  // Job card count fetcher
  const fetchTotalJCCount = useCallback(
    async (parArn, childArn, monthsToFetch) => {
      const apiData = await fetchTotalJC(parArn, childArn, monthsToFetch);

      let count = 0;
      if (apiData?.length === 1) {
        count = apiData[0]?.jc_count?.value || 0;
      } else if (apiData?.length > 1) {
        count = apiData.reduce((sum, month) => {
          return sum + (month?.jc_count?.value || 0);
        }, 0);
      }
      setJcCount(count);
      setHasData(count > 0);
    },
    []
  );

  // Tat Promised Achieved fetcher
  const fetchTatPromiseAchievdData = useCallback(
    async (parArn, childArn, monthsToFetch) => {
      const apiData = await fetchTatPromiseAchievd(
        parArn,
        childArn,
        monthsToFetch
      );

      let dataCount = 0;
      let totalCount = 0;

      // Handle tatPromiseAchievdDataCount (conf_count)
      if (apiData?.tatPromiseAchievdDataCount?.length === 1) {
        dataCount =
          apiData.tatPromiseAchievdDataCount[0]?.conf_count?.value || 0;
      } else if (apiData?.tatPromiseAchievdDataCount?.length > 1) {
        dataCount = apiData.tatPromiseAchievdDataCount.reduce((sum, month) => {
          return sum + (month?.conf_count?.value || 0);
        }, 0);
      }

      // Handle tatPromiseTotalCount (conf_nc_count)
      if (apiData?.tatPromiseTotalCount?.length === 1) {
        totalCount = apiData.tatPromiseTotalCount[0]?.conf_nc_count?.value || 0;
      } else if (apiData?.tatPromiseTotalCount?.length > 1) {
        totalCount = apiData.tatPromiseTotalCount.reduce((sum, month) => {
          return sum + (month?.conf_nc_count?.value || 0);
        }, 0);
      }

      // Calculate percentage
      const percentValue =
        totalCount > 0 ? ((dataCount / totalCount) * 100).toFixed(2) : 0;

      setTatPromiseAchievd({ dataCount, percentValue });
    },
    []
  );

  // Tat Promised Not Achieved fetcher
  const fetchTatPromiseNOTAchievdData = useCallback(
    async (parArn, childArn, monthsToFetch) => {
      const apiData = await fetchTatPromiseNOTAchievd(
        parArn,
        childArn,
        monthsToFetch
      );

      let dataCount = 0;
      let totalCount = 0;

      // Handle tatPromiseNotAchievdDataCount (nc_count)
      if (apiData?.tatPromiseNotAchievdDataCount?.length === 1) {
        dataCount =
          apiData.tatPromiseNotAchievdDataCount[0]?.nc_count?.value || 0;
      } else if (apiData?.tatPromiseNotAchievdDataCount?.length > 1) {
        dataCount = apiData.tatPromiseNotAchievdDataCount.reduce(
          (sum, month) => {
            return sum + (month?.nc_count?.value || 0);
          },
          0
        );
      }

      // Handle tatPromiseTotalCount (conf_nc_count)
      if (apiData?.tatPromiseTotalCount?.length === 1) {
        totalCount = apiData.tatPromiseTotalCount[0]?.conf_nc_count?.value || 0;
      } else if (apiData?.tatPromiseTotalCount?.length > 1) {
        totalCount = apiData.tatPromiseTotalCount.reduce((sum, month) => {
          return sum + (month?.conf_nc_count?.value || 0);
        }, 0);
      }

      // Calculate percentage
      const percentValue =
        totalCount > 0 ? ((dataCount / totalCount) * 100).toFixed(2) : 0;

      setTatPromisenOTAchievd({ dataCount, percentValue });
    },
    []
  );

  // Fetch data for chassis count, job cards, TAT promises, and delay reasons
  const fetchData = useCallback(
    async (parArn, childArn, monthsToFetch) => {
      setLoading(true);
      try {
        await Promise.all([
          fetchChassis(parArn, childArn, monthsToFetch),
          fetchTotalJCCount(parArn, childArn, monthsToFetch),
          fetchTatPromiseAchievdData(parArn, childArn, monthsToFetch),
          fetchTatPromiseNOTAchievdData(parArn, childArn, monthsToFetch),
        ]);
      } catch (error) {
        console.error("Error during batched fetch:", error);
      } finally {
        setLoading(false);
      }
    },
    [
      fetchChassis,
      fetchTotalJCCount,
      fetchTatPromiseAchievdData,
      fetchTatPromiseNOTAchievdData,
    ]
  );

  // SEPARATE function for JobCard delay data that doesn't depend on selectedMonths
  const fetchJobCardDelayResonDataAPI = useCallback(
    async (parArn, childArn) => {
      try {
        const apiData = await fetchJobCardDelayResonData(parArn, childArn);
        if (!apiData) {
          console.error("API returned empty or invalid response:", apiData);
          return;
        }
        setJobCardDelayReson(apiData);
      } catch (error) {
        console.error("Error fetching Job Card Delay Reason Data:", error);
      }
    },
    []
  );

  ///////////////////////////////////////////////////////////////////////////
  const memoizedTableData = useMemo(() => {
    // If no data, return empty arrays
    if (
      !JobCardDelayResonData ||
      Object.keys(JobCardDelayResonData).length === 0
    ) {
      return { data: [], columns: [], accountName: "Unknown" };
    }

    let data = [];
    let columns = [];
    let maxPerReason = {};
    const reasonKeyMap = {};
    let accountName;

    const { accName, ...reasons } = JobCardDelayResonData;
    accountName = accName?.[0]?.key || "Unknown";

    const getSafeKey = (reason) => reason.replace(/[^a-zA-Z0-9]/g, "_");

    const allReasons = Object.keys(reasons);

    const allMonths = Array.from(
      new Set(
        allReasons.flatMap((reason) =>
          reasons[reason].map((entry) => entry?.month).filter(Boolean)
        )
      )
    );

    const reasonMonthMap = {};
    for (const reason of allReasons) {
      reasonMonthMap[reason] = {};
      for (const { month, count } of reasons[reason]) {
        reasonMonthMap[reason][month] = count;
      }
      const counts = Object.values(reasonMonthMap[reason] || {});
      maxPerReason[reason] = Math.max(...counts, 0);
    }

    allReasons.forEach((reason) => {
      reasonKeyMap[getSafeKey(reason)] = reason;
    });

    data = allMonths.map((month) => {
      const row = { accountName, month };
      let total = 0;
      for (const reason of allReasons) {
        const safeKey = getSafeKey(reason);
        const count = reasonMonthMap[reason]?.[month] ?? 0;
        row[safeKey] = count;
        total += count;
      }
      row.total = total;
      return row;
    });

    // Define columns
    const columnHelper = createColumnHelper();
    columns = [
      columnHelper.accessor("month", {
        header: "Month",
        cell: (info) => info.getValue(),
      }),
      ...allReasons.map((reason) => {
        const safeKey = getSafeKey(reason);
        return columnHelper.accessor(safeKey, {
          header: reason,
          cell: (info) => info.getValue(),
        });
      }),
      columnHelper.accessor("total", {
        header: "Total",
        cell: (info) => info.getValue(),
      }),
    ];

    // The getRedShadeWithOpacity function should also be here or imported
    // The original logic for styling will still work, but you need to pass
    // the maxPerReason object down to the table as well.
    // For simplicity, let's keep the styling logic in the table.
    // We will just return the processed data and columns.

    return { data, columns, accountName, reasonKeyMap, maxPerReason };
  }, [JobCardDelayResonData]);

  ///////////////////////////////////////////////////////////////////////////////////////////////////

  // Handle month selection/deselection - optimized with useCallback
  const handleMonthToggle = useCallback(
    (monthValue) => {
      const monthOnly = monthValue.split("-")[0];

      setSelectedMonths((prevSelected) => {
        const isSelected = prevSelected.includes(monthOnly);
        let newSelected;

        if (isSelected) {
          // Removing the month
          newSelected = prevSelected.filter((month) => month !== monthOnly);
          // If all months are deselected, keep current month
          if (newSelected.length === 0) {
            newSelected = [currentMonth];
          }
        } else {
          // Adding the month
          newSelected = [...prevSelected, monthOnly];
        }

        return newSelected;
      });
    },
    [currentMonth]
  );

  // Get display text for the dropdown button - memoized
  const getDisplayText = useMemo(() => {
    if (selectedMonths.length === 0) {
      return currentMonth;
    }
    if (selectedMonths.length === 1) {
      return selectedMonths[0];
    }
    return `${selectedMonths.length} months selected`;
  }, [selectedMonths, currentMonth]);

  // Initialize current month and selected months
  useEffect(() => {
    const currentMon = getCurrentMonth();
    setCurrentMonth(currentMon);
    setSelectedMonths([currentMon]);
  }, [getCurrentMonth]);

  // Fetch months and initial data on mount
  useEffect(() => {
    fetchMonth();
  }, [fetchMonth]);

  // CRITICAL FIX: Only fetch JobCard delay data ONCE on mount, not on every selectedMonths change
  useEffect(() => {
    fetchJobCardDelayResonDataAPI(parArn, childArn);
  }, [fetchJobCardDelayResonDataAPI, parArn, childArn]);

  // Fetch data when selected months change - debounced to prevent excessive API calls
  const debouncedFetchData = useMemo(
    () =>
      debounce((parArn, childArn, monthsToFetch) => {
        fetchData(parArn, childArn, monthsToFetch);
      }, 300),
    [fetchData]
  );

  useEffect(() => {
    if (selectedMonths.length > 0) {
      debouncedFetchData(parArn, childArn, selectedMonths);
    }

    // Cleanup debounce on unmount
    return () => {
      debouncedFetchData.cancel();
    };
  }, [
    selectedMonths,
    parArn,
    childArn,
    debouncedFetchData,
  ]);

  return (
    <div className="">
      <div className="p-2 py-1 px-2  bg-[#056DB3]  font-bold rounded-t-md w-96 md:w-full  text-white">
        <div className="dropdown-container text-black flex w-full">
          <h1 className="pl-4 w-full font-bold text-white">
            Service Charter Performance
          </h1>

          {/* Custom Multi-Select Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              className="month-dropdown rounded-md px-3  bg-white text-black min-w-[150px] text-center border border-gray-300 flex items-center justify-between"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              type="button"
            >
              <span className="truncate">{getDisplayText}</span>
              <span
                className={`ml-2 transform transition-transform ${isDropdownOpen ? "rotate-180" : ""
                  }`}
              >
                ▼
              </span>
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                {months.map((item, i) => {
                  const monthOnly = item.month.split("-")[0];
                  const isSelected = selectedMonths.includes(monthOnly);

                  return (
                    <label
                      key={i}
                      className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleMonthToggle(item.month)}
                        className="mr-2"
                      />
                      <span className="text-sm">{item.month}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* f */}

      <div
        className={`w-full justify-center items-center gap-4 block md:${open
          ? "flex flex-col"
          : "block md:flex lg:flex-row xl:flex-row"
          }`}
      >
        <div
          className={`justify-center ${hasData
            ? open
              ? "w-full"
              : "md:w-1/2 w-full"
            : "w-full"
            }`}

        >
          <Contentcards heading={"Conf./NC"} content={<Confcard />} />
        </div>
        <div
          className={`justify-center ${hasData
            ? open
              ? "w-full"
              : "md:w-1/2 w-full"
            : "w-full"
            }`}

        >
          <Contentcards
            heading={"Delay Reason"}
            content={<Delayreasoncard selectedMonth={selectedMonths} />}
          />
        </div>
      </div>

      <Contentcards2
        heading="Job Card Delay Reason"
        content={
          loading ? (
            <div className="p-4 text-center">Loading chart data...</div>
          ) : (
            <TanStackTable
              data={memoizedTableData.data}
              columns={memoizedTableData.columns}
              accountName={memoizedTableData.accountName}
              reasonKeyMap={memoizedTableData.reasonKeyMap}
              maxPerReason={memoizedTableData.maxPerReason}
            />
          )
        }
      />
    </div >
  );
};

export default ServicePage;
